/**
 * Environment Indicator Installer
 *
 * Overlays a colored badge on the favicon for non-production environments.
 * Intended to be invoked from a per-app Quasar boot file:
 *
 * @example
 *   // apps/ui/src/boot/environment-indicator.ts
 *   import { boot } from 'quasar/wrappers';
 *   import { useEnvironmentConfig } from '@shared/vue/composables';
 *   import { installEnvironmentIndicator } from '@shared/vue/utils/environmentIndicator';
 *
 *   export default boot(() => {
 *     installEnvironmentIndicator({ env: useEnvironmentConfig() });
 *   });
 *
 * The environment name in the browser-tab title is handled separately by the
 * shared title formatter (`formatDocumentTitle`), which folds the env into the
 * brand (`EMALINO Local`) rather than a `[ENV]` prefix. The boot wrapper stays
 * per-app because Quasar's `boot()` helper isn't available from `_shared`.
 */

import { devWarn } from './debug';
import type { useEnvironmentConfig } from '@shared/vue/composables';

type EnvConfig = ReturnType<typeof useEnvironmentConfig>;

export interface EnvironmentIndicatorOptions {
  env: EnvConfig;
}

/**
 * Install the environment indicator (favicon badge). No-op in production and
 * wherever the env config disables the favicon badge.
 */
export function installEnvironmentIndicator(opts: EnvironmentIndicatorOptions): void {
  const { shouldShowFaviconBadge, getEnvironmentColor } = opts.env;

  if (!shouldShowFaviconBadge.value) return;

  function addFaviconBadge(): void {
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!favicon || !favicon.href) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = favicon.href;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const size = Math.max(img.width, img.height, 32);
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.drawImage(img, 0, 0, size, size);

      const isDark = document.body.classList.contains('body--dark');
      const badgeColor = getEnvironmentColor(isDark);
      const badgeSize = size * 0.35;
      const badgeX = size - badgeSize / 2 - 2;
      const badgeY = size - badgeSize / 2 - 2;

      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 3;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(badgeX, badgeY, badgeSize / 2 + 1, 0, 2 * Math.PI);
      ctx.fill();

      ctx.shadowColor = 'transparent';
      ctx.fillStyle = badgeColor;
      ctx.beginPath();
      ctx.arc(badgeX, badgeY, badgeSize / 2, 0, 2 * Math.PI);
      ctx.fill();

      favicon.href = canvas.toDataURL();
    };

    img.onerror = () => {
      devWarn('[Environment Indicator] Could not load favicon for badge overlay');
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', addFaviconBadge);
  } else {
    addFaviconBadge();
  }
}
